// Team configuration - single source of truth for team colors and name words

// Team colors with their display hex values
export interface TeamColorConfig {
    id: string;
    hex: string;
}

export const TEAM_COLORS_CONFIG: TeamColorConfig[] = [
    { id: 'rose-400', hex: '#fb7185' },
    { id: 'red-400', hex: '#f87171' },
    { id: 'orange-400', hex: '#fb923c' },
    { id: 'amber-400', hex: '#fbbf24' },
    { id: 'yellow-400', hex: '#facc15' },
    { id: 'lime-400', hex: '#a3e635' },
    { id: 'green-400', hex: '#4ade80' },
    { id: 'teal-400', hex: '#2dd4bf' },
    { id: 'cyan-400', hex: '#22d3ee' },
    { id: 'sky-400', hex: '#38bdf8' },
    { id: 'indigo-400', hex: '#818cf8' },

    { id: 'rose-800', hex: '#9f1239' },
    { id: 'red-800', hex: '#991b1b' },
    { id: 'orange-800', hex: '#9a3412' },
    { id: 'amber-800', hex: '#92400e' },
    { id: 'yellow-800', hex: '#854d0e' },
    { id: 'lime-800', hex: '#3f6212' },
    { id: 'green-800', hex: '#166534' },
    { id: 'teal-800', hex: '#115e59' },
    { id: 'cyan-800', hex: '#155e75' },
    { id: 'sky-800', hex: '#075985' },
    { id: 'indigo-800', hex: '#3730a3' },
];


// Get the list of color IDs for schema validation
export const TEAM_COLOR_IDS = TEAM_COLORS_CONFIG.map(c => c.id) as readonly string[];

// Helper to get hex color from color ID
export function getTeamColorHex(colorId: string): string {
    const color = TEAM_COLORS_CONFIG.find(c => c.id === colorId);
    return color?.hex ?? '#EF4444'; // Default to red if not found
}

// Helper to get color config by ID
export function getTeamColorConfig(colorId: string): TeamColorConfig | undefined {
    return TEAM_COLORS_CONFIG.find(c => c.id === colorId);
}

// Team name word categories (based on FFXIV Race Chocobo Name List)
export type TeamNameWordCategory =
    | 'colors'
    | 'materials'
    | 'concreteAdj'
    | 'abstractAdj'
    | 'appearance'
    | 'personality'
    | 'concepts'
    | 'flora'
    | 'fauna'
    | 'landSky'
    | 'weather'
    | 'phenomena'
    | 'battle'
    | 'racing'
    | 'titles'
    | 'numbers'
    | 'properNouns'
    | 'eorzea'
    | 'others';

export interface TeamNameWord {
    word: string;
    category: TeamNameWordCategory;
}

// FFXIV Race Chocobo Name List - can be used for both first and second word
export const TEAM_NAME_WORDS: TeamNameWord[] = [
    // Colors
    { word: 'Black', category: 'colors' },
    { word: 'Blue', category: 'colors' },
    { word: 'Cerulean', category: 'colors' },
    { word: 'Gold', category: 'colors' },
    { word: 'Ivory', category: 'colors' },
    { word: 'Orange', category: 'colors' },
    { word: 'Silver', category: 'colors' },
    { word: 'White', category: 'colors' },
    { word: 'Azure', category: 'colors' },
    { word: 'Chestnut', category: 'colors' },
    { word: 'Cobalt', category: 'colors' },
    { word: 'Crimson', category: 'colors' },
    { word: 'Green', category: 'colors' },
    { word: 'Grey', category: 'colors' },
    { word: 'Indigo', category: 'colors' },
    { word: 'Pastel', category: 'colors' },
    { word: 'Red', category: 'colors' },
    { word: 'Scarlet', category: 'colors' },
    { word: 'Sorrel', category: 'colors' },
    { word: 'Violet', category: 'colors' },
    { word: 'Xanthous', category: 'colors' },
    { word: 'Yellow', category: 'colors' },

    // Materials
    { word: 'Amber', category: 'materials' },
    { word: 'Amethyst', category: 'materials' },
    { word: 'Bronze', category: 'materials' },
    { word: 'Cinder', category: 'materials' },
    { word: 'Copper', category: 'materials' },
    { word: 'Crystal', category: 'materials' },
    { word: 'Darksteel', category: 'materials' },
    { word: 'Diamond', category: 'materials' },
    { word: 'Electrum', category: 'materials' },
    { word: 'Ember', category: 'materials' },
    { word: 'Garnet', category: 'materials' },
    { word: 'Iron', category: 'materials' },
    { word: 'Jade', category: 'materials' },
    { word: 'Mythril', category: 'materials' },
    { word: 'Obsidian', category: 'materials' },
    { word: 'Platinum', category: 'materials' },
    { word: 'Quartz', category: 'materials' },
    { word: 'Ruby', category: 'materials' },
    { word: 'Stone', category: 'materials' },
    { word: 'Turquoise', category: 'materials' },
    { word: 'Velvet', category: 'materials' },
    { word: 'Wootz', category: 'materials' },

    // Concrete Adjectives
    { word: 'Agile', category: 'concreteAdj' },
    { word: 'Avian', category: 'concreteAdj' },
    { word: 'Big', category: 'concreteAdj' },
    { word: 'Blazing', category: 'concreteAdj' },
    { word: 'Blinding', category: 'concreteAdj' },
    { word: 'Bloody', category: 'concreteAdj' },
    { word: 'Crackling', category: 'concreteAdj' },
    { word: 'Dancing', category: 'concreteAdj' },
    { word: 'Dark', category: 'concreteAdj' },
    { word: 'Drowned', category: 'concreteAdj' },
    { word: 'Eastern', category: 'concreteAdj' },
    { word: 'Elder', category: 'concreteAdj' },
    { word: 'Fast', category: 'concreteAdj' },
    { word: 'Fiery', category: 'concreteAdj' },
    { word: 'Fleet', category: 'concreteAdj' },
    { word: 'Flying', category: 'concreteAdj' },
    { word: 'Frosty', category: 'concreteAdj' },
    { word: 'Frozen', category: 'concreteAdj' },
    { word: 'Hot', category: 'concreteAdj' },
    { word: 'Icy', category: 'concreteAdj' },
    { word: 'Jumping', category: 'concreteAdj' },
    { word: 'Leaping', category: 'concreteAdj' },
    { word: 'Limber', category: 'concreteAdj' },
    { word: 'Long', category: 'concreteAdj' },
    { word: 'Nimble', category: 'concreteAdj' },
    { word: 'Northern', category: 'concreteAdj' },
    { word: 'Old', category: 'concreteAdj' },
    { word: 'Quick', category: 'concreteAdj' },
    { word: 'Radiant', category: 'concreteAdj' },
    { word: 'Raging', category: 'concreteAdj' },
    { word: 'Rich', category: 'concreteAdj' },
    { word: 'Rising', category: 'concreteAdj' },
    { word: 'Shining', category: 'concreteAdj' },
    { word: 'Shooting', category: 'concreteAdj' },
    { word: 'Silky', category: 'concreteAdj' },
    { word: 'Smoking', category: 'concreteAdj' },
    { word: 'Snowy', category: 'concreteAdj' },
    { word: 'Southern', category: 'concreteAdj' },
    { word: 'Sparkling', category: 'concreteAdj' },
    { word: 'Swift', category: 'concreteAdj' },
    { word: 'Sylvan', category: 'concreteAdj' },
    { word: 'Turbulent', category: 'concreteAdj' },
    { word: 'Twinkling', category: 'concreteAdj' },
    { word: 'Vivid', category: 'concreteAdj' },
    { word: 'Western', category: 'concreteAdj' },
    { word: 'Winning', category: 'concreteAdj' },

    // Abstract Adjectives
    { word: 'Aetherial', category: 'abstractAdj' },
    { word: 'Best', category: 'abstractAdj' },
    { word: 'Blistering', category: 'abstractAdj' },
    { word: 'Cool', category: 'abstractAdj' },
    { word: 'Deep', category: 'abstractAdj' },
    { word: 'Easy', category: 'abstractAdj' },
    { word: 'Eternal', category: 'abstractAdj' },
    { word: 'Excellent', category: 'abstractAdj' },
    { word: 'Fantastic', category: 'abstractAdj' },
    { word: 'Final', category: 'abstractAdj' },
    { word: 'Fine', category: 'abstractAdj' },
    { word: 'Good', category: 'abstractAdj' },
    { word: 'Hyper', category: 'abstractAdj' },
    { word: 'Immortal', category: 'abstractAdj' },
    { word: 'Imperial', category: 'abstractAdj' },
    { word: 'Invincible', category: 'abstractAdj' },
    { word: 'Last', category: 'abstractAdj' },
    { word: 'Magic', category: 'abstractAdj' },
    { word: 'Magnificent', category: 'abstractAdj' },
    { word: 'Max', category: 'abstractAdj' },
    { word: 'Maximum', category: 'abstractAdj' },
    { word: 'Natural', category: 'abstractAdj' },
    { word: 'Negative', category: 'abstractAdj' },
    { word: 'Neo', category: 'abstractAdj' },
    { word: 'Noble', category: 'abstractAdj' },
    { word: 'Optimal', category: 'abstractAdj' },
    { word: 'Perfect', category: 'abstractAdj' },
    { word: 'Poetic', category: 'abstractAdj' },
    { word: 'Positive', category: 'abstractAdj' },
    { word: 'Premium', category: 'abstractAdj' },
    { word: 'Pure', category: 'abstractAdj' },
    { word: 'Royal', category: 'abstractAdj' },
    { word: 'Saint', category: 'abstractAdj' },
    { word: 'Special', category: 'abstractAdj' },
    { word: 'Super', category: 'abstractAdj' },
    { word: 'Sweet', category: 'abstractAdj' },
    { word: 'Transient', category: 'abstractAdj' },
    { word: 'Ultimate', category: 'abstractAdj' },
    { word: 'Ultra', category: 'abstractAdj' },
    { word: 'Vorpal', category: 'abstractAdj' },
    { word: 'Wicked', category: 'abstractAdj' },
    { word: 'Wonderful', category: 'abstractAdj' },

    // Appearance
    { word: 'Beautiful', category: 'appearance' },
    { word: 'Charisma', category: 'appearance' },
    { word: 'Chic', category: 'appearance' },
    { word: 'Dandy', category: 'appearance' },
    { word: 'Elegant', category: 'appearance' },
    { word: 'Fat', category: 'appearance' },
    { word: 'Feathered', category: 'appearance' },
    { word: 'Gallant', category: 'appearance' },
    { word: 'Giant', category: 'appearance' },
    { word: 'Gorgeous', category: 'appearance' },
    { word: 'Nice', category: 'appearance' },
    { word: 'Pretty', category: 'appearance' },
    { word: 'Princely', category: 'appearance' },
    { word: 'Stylish', category: 'appearance' },
    { word: 'Winged', category: 'appearance' },
    { word: 'Young', category: 'appearance' },

    // Personality
    { word: 'Ambitious', category: 'personality' },
    { word: 'Angelic', category: 'personality' },
    { word: 'Angry', category: 'personality' },
    { word: 'Ardent', category: 'personality' },
    { word: 'Benevolent', category: 'personality' },
    { word: 'Bold', category: 'personality' },
    { word: 'Brave', category: 'personality' },
    { word: 'Brilliant', category: 'personality' },
    { word: 'Candid', category: 'personality' },
    { word: 'Daring', category: 'personality' },
    { word: 'Devilish', category: 'personality' },
    { word: 'Dutiful', category: 'personality' },
    { word: 'Eager', category: 'personality' },
    { word: 'Evil', category: 'personality' },
    { word: 'Great', category: 'personality' },
    { word: 'Grim', category: 'personality' },
    { word: 'Humble', category: 'personality' },
    { word: 'Innocent', category: 'personality' },
    { word: 'Jolly', category: 'personality' },
    { word: 'Lucky', category: 'personality' },
    { word: 'Malevolent', category: 'personality' },
    { word: 'Melancholy', category: 'personality' },
    { word: 'Merciless', category: 'personality' },
    { word: 'Merry', category: 'personality' },
    { word: 'Patient', category: 'personality' },
    { word: 'Pensive', category: 'personality' },
    { word: 'Reckless', category: 'personality' },
    { word: 'Restless', category: 'personality' },
    { word: 'Robust', category: 'personality' },
    { word: 'Rugged', category: 'personality' },
    { word: 'Silent', category: 'personality' },
    { word: 'Somber', category: 'personality' },
    { word: 'Tempered', category: 'personality' },
    { word: 'Touched', category: 'personality' },
    { word: 'Tranquil', category: 'personality' },
    { word: 'Wild', category: 'personality' },

    // Concepts
    { word: 'Animus', category: 'concepts' },
    { word: 'Atma', category: 'concepts' },
    { word: 'Beauty', category: 'concepts' },
    { word: 'Brilliance', category: 'concepts' },
    { word: 'Chaos', category: 'concepts' },
    { word: 'Destruction', category: 'concepts' },
    { word: 'Divine', category: 'concepts' },
    { word: 'Dream', category: 'concepts' },
    { word: 'End', category: 'concepts' },
    { word: 'Fortune', category: 'concepts' },
    { word: 'Fury', category: 'concepts' },
    { word: 'Genesis', category: 'concepts' },
    { word: 'Groove', category: 'concepts' },
    { word: 'Harmony', category: 'concepts' },
    { word: 'Hope', category: 'concepts' },
    { word: 'Illusion', category: 'concepts' },
    { word: 'Joy', category: 'concepts' },
    { word: 'Justice', category: 'concepts' },
    { word: 'Legend', category: 'concepts' },
    { word: 'Liberty', category: 'concepts' },
    { word: 'Miracle', category: 'concepts' },
    { word: 'Nexus', category: 'concepts' },
    { word: 'Novus', category: 'concepts' },
    { word: 'Oracle', category: 'concepts' },
    { word: 'Passion', category: 'concepts' },
    { word: 'Peace', category: 'concepts' },
    { word: 'Spirit', category: 'concepts' },
    { word: 'Terror', category: 'concepts' },
    { word: 'Verve', category: 'concepts' },
    { word: 'Wonder', category: 'concepts' },

    // Flora
    { word: 'Ebony', category: 'flora' },
    { word: 'Flora', category: 'flora' },
    { word: 'Flower', category: 'flora' },
    { word: 'Ginger', category: 'flora' },
    { word: 'Iris', category: 'flora' },
    { word: 'Jasmine', category: 'flora' },
    { word: 'Laurel', category: 'flora' },
    { word: 'Lily', category: 'flora' },
    { word: 'Mandragora', category: 'flora' },
    { word: 'Mimosa', category: 'flora' },
    { word: 'Olive', category: 'flora' },
    { word: 'Orchid', category: 'flora' },
    { word: 'Paprika', category: 'flora' },
    { word: 'Rice', category: 'flora' },
    { word: 'Rose', category: 'flora' },
    { word: 'Saffron', category: 'flora' },
    { word: 'Sakura', category: 'flora' },

    // Fauna
    { word: 'Adder', category: 'fauna' },
    { word: 'Angel', category: 'fauna' },
    { word: 'Apkallu', category: 'fauna' },
    { word: 'Bat', category: 'fauna' },
    { word: 'Beak', category: 'fauna' },
    { word: 'Beetle', category: 'fauna' },
    { word: 'Blood', category: 'fauna' },
    { word: 'Bone', category: 'fauna' },
    { word: 'Claw', category: 'fauna' },
    { word: 'Condor', category: 'fauna' },
    { word: 'Devil', category: 'fauna' },
    { word: 'Dodo', category: 'fauna' },
    { word: 'Dragon', category: 'fauna' },
    { word: 'Eagle', category: 'fauna' },
    { word: 'Eye', category: 'fauna' },
    { word: 'Face', category: 'fauna' },
    { word: 'Fairy', category: 'fauna' },
    { word: 'Feather', category: 'fauna' },
    { word: 'Fledgling', category: 'fauna' },
    { word: 'Foot', category: 'fauna' },
    { word: 'Fox', category: 'fauna' },
    { word: 'Ghost', category: 'fauna' },
    { word: 'Goose', category: 'fauna' },
    { word: 'Hatchling', category: 'fauna' },
    { word: 'Heart', category: 'fauna' },
    { word: 'Jay', category: 'fauna' },
    { word: 'Leg', category: 'fauna' },
    { word: 'Manticore', category: 'fauna' },
    { word: 'Minotaur', category: 'fauna' },
    { word: 'Mustang', category: 'fauna' },
    { word: 'Pegasus', category: 'fauna' },
    { word: 'Pelican', category: 'fauna' },
    { word: 'Phoenix', category: 'fauna' },
    { word: 'Phurble', category: 'fauna' },
    { word: 'Plumage', category: 'fauna' },
    { word: 'Pony', category: 'fauna' },
    { word: 'Raven', category: 'fauna' },
    { word: 'Robin', category: 'fauna' },
    { word: 'Serpent', category: 'fauna' },
    { word: 'Stallion', category: 'fauna' },
    { word: 'Steed', category: 'fauna' },
    { word: 'Tail', category: 'fauna' },
    { word: 'Talon', category: 'fauna' },
    { word: 'Trotter', category: 'fauna' },
    { word: 'Unicorn', category: 'fauna' },
    { word: 'Vulture', category: 'fauna' },
    { word: 'Wing', category: 'fauna' },

    // Land & Sky
    { word: 'Comet', category: 'landSky' },
    { word: 'Crag', category: 'landSky' },
    { word: 'Desert', category: 'landSky' },
    { word: 'East', category: 'landSky' },
    { word: 'Eclipse', category: 'landSky' },
    { word: 'Flood', category: 'landSky' },
    { word: 'Forest', category: 'landSky' },
    { word: 'Galaxy', category: 'landSky' },
    { word: 'Highland', category: 'landSky' },
    { word: 'Jungle', category: 'landSky' },
    { word: 'Jupiter', category: 'landSky' },
    { word: 'Luna', category: 'landSky' },
    { word: 'Lunar', category: 'landSky' },
    { word: 'Mars', category: 'landSky' },
    { word: 'Mercury', category: 'landSky' },
    { word: 'Meteor', category: 'landSky' },
    { word: 'Moon', category: 'landSky' },
    { word: 'Nebula', category: 'landSky' },
    { word: 'Nebulous', category: 'landSky' },
    { word: 'Neptune', category: 'landSky' },
    { word: 'Nether', category: 'landSky' },
    { word: 'North', category: 'landSky' },
    { word: 'Nova', category: 'landSky' },
    { word: 'Pluto', category: 'landSky' },
    { word: 'Quasar', category: 'landSky' },
    { word: 'Road', category: 'landSky' },
    { word: 'Saturn', category: 'landSky' },
    { word: 'Sea', category: 'landSky' },
    { word: 'Solar', category: 'landSky' },
    { word: 'South', category: 'landSky' },
    { word: 'Star', category: 'landSky' },
    { word: 'Stardust', category: 'landSky' },
    { word: 'Stream', category: 'landSky' },
    { word: 'Sun', category: 'landSky' },
    { word: 'Titan', category: 'landSky' },
    { word: 'Universe', category: 'landSky' },
    { word: 'Uranus', category: 'landSky' },
    { word: 'Vega', category: 'landSky' },
    { word: 'Venus', category: 'landSky' },
    { word: 'West', category: 'landSky' },

    // Weather
    { word: 'Air', category: 'weather' },
    { word: 'Autumn', category: 'weather' },
    { word: 'Blizzard', category: 'weather' },
    { word: 'Breeze', category: 'weather' },
    { word: 'Cloud', category: 'weather' },
    { word: 'Cyclone', category: 'weather' },
    { word: 'Echo', category: 'weather' },
    { word: 'Flurry', category: 'weather' },
    { word: 'Fog', category: 'weather' },
    { word: 'Frost', category: 'weather' },
    { word: 'Gale', category: 'weather' },
    { word: 'Gloom', category: 'weather' },
    { word: 'Gust', category: 'weather' },
    { word: 'Hurricane', category: 'weather' },
    { word: 'Lightning', category: 'weather' },
    { word: 'Mirage', category: 'weather' },
    { word: 'Mistral', category: 'weather' },
    { word: 'Nature', category: 'weather' },
    { word: 'Night', category: 'weather' },
    { word: 'Quake', category: 'weather' },
    { word: 'Rain', category: 'weather' },
    { word: 'Rainbow', category: 'weather' },
    { word: 'Sandstorm', category: 'weather' },
    { word: 'Sky', category: 'weather' },
    { word: 'Snow', category: 'weather' },
    { word: 'Snowstorm', category: 'weather' },
    { word: 'Spring', category: 'weather' },
    { word: 'Storm', category: 'weather' },
    { word: 'Summer', category: 'weather' },
    { word: 'Tailwind', category: 'weather' },
    { word: 'Tempest', category: 'weather' },
    { word: 'Thunder', category: 'weather' },
    { word: 'Tornado', category: 'weather' },
    { word: 'Typhoon', category: 'weather' },
    { word: 'Wave', category: 'weather' },
    { word: 'Whirlwind', category: 'weather' },
    { word: 'Wind', category: 'weather' },
    { word: 'Winter', category: 'weather' },
    { word: 'Zephyr', category: 'weather' },

    // Phenomena
    { word: 'Blaze', category: 'phenomena' },
    { word: 'Burst', category: 'phenomena' },
    { word: 'Fire', category: 'phenomena' },
    { word: 'Flare', category: 'phenomena' },
    { word: 'Freeze', category: 'phenomena' },
    { word: 'Ice', category: 'phenomena' },
    { word: 'Impact', category: 'phenomena' },
    { word: 'Impulse', category: 'phenomena' },
    { word: 'Inferno', category: 'phenomena' },
    { word: 'Light', category: 'phenomena' },
    { word: 'Maelstrom', category: 'phenomena' },
    { word: 'Quantum', category: 'phenomena' },
    { word: 'Ray', category: 'phenomena' },
    { word: 'Shadow', category: 'phenomena' },
    { word: 'Spark', category: 'phenomena' },
    { word: 'Vortex', category: 'phenomena' },

    // Battle
    { word: 'Ace', category: 'battle' },
    { word: 'Admiral', category: 'battle' },
    { word: 'Archer', category: 'battle' },
    { word: 'Arrow', category: 'battle' },
    { word: 'Avenger', category: 'battle' },
    { word: 'Axe', category: 'battle' },
    { word: 'Battle', category: 'battle' },
    { word: 'Blade', category: 'battle' },
    { word: 'Blitz', category: 'battle' },
    { word: 'Blitzer', category: 'battle' },
    { word: 'Captain', category: 'battle' },
    { word: 'Centurion', category: 'battle' },
    { word: 'Charger', category: 'battle' },
    { word: 'Commander', category: 'battle' },
    { word: 'Crusader', category: 'battle' },
    { word: 'Dagger', category: 'battle' },
    { word: 'Destroyer', category: 'battle' },
    { word: 'Exterminator', category: 'battle' },
    { word: 'Fighter', category: 'battle' },
    { word: 'Firecracker', category: 'battle' },
    { word: 'General', category: 'battle' },
    { word: 'Javelin', category: 'battle' },
    { word: 'Knife', category: 'battle' },
    { word: 'Lancer', category: 'battle' },
    { word: 'Legatus', category: 'battle' },
    { word: 'Lieutenant', category: 'battle' },
    { word: 'Marshal', category: 'battle' },
    { word: 'Ninja', category: 'battle' },
    { word: 'Outlaw', category: 'battle' },
    { word: 'Private', category: 'battle' },
    { word: 'Samurai', category: 'battle' },
    { word: 'Sergeant', category: 'battle' },
    { word: 'Shield', category: 'battle' },
    { word: 'Shooter', category: 'battle' },
    { word: 'Spear', category: 'battle' },
    { word: 'Strike', category: 'battle' },
    { word: 'Sword', category: 'battle' },
    { word: 'Torpedo', category: 'battle' },
    { word: 'Trooper', category: 'battle' },
    { word: 'War', category: 'battle' },
    { word: 'Warrior', category: 'battle' },

    // Racing
    { word: 'Ballistic', category: 'racing' },
    { word: 'Cart', category: 'racing' },
    { word: 'Challenger', category: 'racing' },
    { word: 'Champion', category: 'racing' },
    { word: 'Chance', category: 'racing' },
    { word: 'Chariot', category: 'racing' },
    { word: 'Clipper', category: 'racing' },
    { word: 'Crown', category: 'racing' },
    { word: 'Drafter', category: 'racing' },
    { word: 'Express', category: 'racing' },
    { word: 'Flight', category: 'racing' },
    { word: 'Flyer', category: 'racing' },
    { word: 'Galloper', category: 'racing' },
    { word: 'Glory', category: 'racing' },
    { word: 'Goer', category: 'racing' },
    { word: 'Jet', category: 'racing' },
    { word: 'Leaper', category: 'racing' },
    { word: 'Motion', category: 'racing' },
    { word: 'Passer', category: 'racing' },
    { word: 'Pelter', category: 'racing' },
    { word: 'Power', category: 'racing' },
    { word: 'Prancer', category: 'racing' },
    { word: 'Roadster', category: 'racing' },
    { word: 'Runner', category: 'racing' },
    { word: 'Speed', category: 'racing' },
    { word: 'Speedster', category: 'racing' },
    { word: 'Sprinter', category: 'racing' },
    { word: 'Triumph', category: 'racing' },
    { word: 'Turbo', category: 'racing' },
    { word: 'Victor', category: 'racing' },
    { word: 'Victory', category: 'racing' },
    { word: 'Walker', category: 'racing' },
    { word: 'Winner', category: 'racing' },

    // Titles
    { word: 'Baron', category: 'titles' },
    { word: 'Baroness', category: 'titles' },
    { word: 'Boy', category: 'titles' },
    { word: 'Count', category: 'titles' },
    { word: 'Countess', category: 'titles' },
    { word: 'Dame', category: 'titles' },
    { word: 'Dancer', category: 'titles' },
    { word: 'Duchess', category: 'titles' },
    { word: 'Duke', category: 'titles' },
    { word: 'Emperor', category: 'titles' },
    { word: 'Empress', category: 'titles' },
    { word: 'Esquire', category: 'titles' },
    { word: 'Friend', category: 'titles' },
    { word: 'Girl', category: 'titles' },
    { word: 'Hero', category: 'titles' },
    { word: 'Heroine', category: 'titles' },
    { word: 'Idol', category: 'titles' },
    { word: 'King', category: 'titles' },
    { word: 'Lady', category: 'titles' },
    { word: 'Lord', category: 'titles' },
    { word: 'Maestro', category: 'titles' },
    { word: 'Maharaja', category: 'titles' },
    { word: 'Majesty', category: 'titles' },
    { word: 'Mama', category: 'titles' },
    { word: 'Marquis', category: 'titles' },
    { word: 'Marquise', category: 'titles' },
    { word: 'Master', category: 'titles' },
    { word: 'Miss', category: 'titles' },
    { word: 'Mister', category: 'titles' },
    { word: 'Nana', category: 'titles' },
    { word: 'Papa', category: 'titles' },
    { word: 'Partner', category: 'titles' },
    { word: 'Pendragon', category: 'titles' },
    { word: 'Prince', category: 'titles' },
    { word: 'Princess', category: 'titles' },
    { word: 'Queen', category: 'titles' },
    { word: 'Sage', category: 'titles' },
    { word: 'Scion', category: 'titles' },
    { word: 'Sultan', category: 'titles' },
    { word: 'Sultana', category: 'titles' },
    { word: 'Traveler', category: 'titles' },
    { word: 'Tycoon', category: 'titles' },

    // Numbers
    { word: 'I', category: 'numbers' },
    { word: 'II', category: 'numbers' },
    { word: 'III', category: 'numbers' },
    { word: 'IV', category: 'numbers' },
    { word: 'V', category: 'numbers' },
    { word: 'VI', category: 'numbers' },
    { word: 'VII', category: 'numbers' },
    { word: 'VIII', category: 'numbers' },
    { word: 'IX', category: 'numbers' },
    { word: 'X', category: 'numbers' },
    { word: 'XI', category: 'numbers' },
    { word: 'XII', category: 'numbers' },
    { word: 'XIII', category: 'numbers' },
    { word: 'XIV', category: 'numbers' },
    { word: 'Infinity', category: 'numbers' },
    { word: 'Million', category: 'numbers' },
    { word: 'Millennium', category: 'numbers' },
    { word: 'One', category: 'numbers' },
    { word: 'Ten', category: 'numbers' },
    { word: 'Zero', category: 'numbers' },
    { word: 'Zillion', category: 'numbers' },

    // Proper Nouns
    { word: 'Asuka', category: 'properNouns' },
    { word: 'Brian', category: 'properNouns' },
    { word: 'Bucephalus', category: 'properNouns' },
    { word: 'Diana', category: 'properNouns' },
    { word: 'Goemon', category: 'properNouns' },
    { word: 'Gonzales', category: 'properNouns' },
    { word: 'Helidor', category: 'properNouns' },
    { word: 'Hermes', category: 'properNouns' },
    { word: 'Hyperion', category: 'properNouns' },
    { word: 'Izumo', category: 'properNouns' },
    { word: 'Komachi', category: 'properNouns' },
    { word: 'Musashi', category: 'properNouns' },
    { word: 'Nemesis', category: 'properNouns' },
    { word: 'Rosinante', category: 'properNouns' },
    { word: 'Selene', category: 'properNouns' },
    { word: 'Tristan', category: 'properNouns' },
    { word: 'Valentine', category: 'properNouns' },
    { word: 'Xanthe', category: 'properNouns' },
    { word: 'Yamato', category: 'properNouns' },

    // Eorzea
    { word: 'Eorzean', category: 'eorzea' },
    { word: 'Garlean', category: 'eorzea' },
    { word: 'Gridanian', category: 'eorzea' },
    { word: 'Gysahl', category: 'eorzea' },
    { word: 'Ishgardian', category: 'eorzea' },
    { word: 'Ixali', category: 'eorzea' },
    { word: 'Lominsan', category: 'eorzea' },
    { word: 'Sharlayan', category: 'eorzea' },
    { word: 'Thavnairian', category: 'eorzea' },
    { word: 'Twelveswood', category: 'eorzea' },
    { word: "Ul'dahn", category: 'eorzea' },

    // Others
    { word: 'Alpha', category: 'others' },
    { word: 'Biscuit', category: 'others' },
    { word: 'Bonbon', category: 'others' },
    { word: 'Cap', category: 'others' },
    { word: 'Caramel', category: 'others' },
    { word: 'Craft', category: 'others' },
    { word: 'Cry', category: 'others' },
    { word: 'Dance', category: 'others' },
    { word: 'Emblem', category: 'others' },
    { word: 'Kweh', category: 'others' },
    { word: 'Line', category: 'others' },
    { word: 'Melody', category: 'others' },
    { word: 'Omega', category: 'others' },
    { word: 'Point', category: 'others' },
    { word: 'Rhapsody', category: 'others' },
    { word: 'Saddle', category: 'others' },
    { word: 'Sweep', category: 'others' },
    { word: 'The', category: 'others' },
    { word: 'Ticket', category: 'others' },
    { word: 'Wark', category: 'others' },
    { word: 'Whisper', category: 'others' },
    { word: 'Whorl', category: 'others' },
];

// Get all unique categories
export function getWordCategories(): TeamNameWordCategory[] {
    const categories = new Set(TEAM_NAME_WORDS.map(w => w.category));
    return Array.from(categories);
}

// Get words filtered by category
export function getWordsByCategory(category?: TeamNameWordCategory): TeamNameWord[] {
    if (!category) return TEAM_NAME_WORDS;
    return TEAM_NAME_WORDS.filter(w => w.category === category);
}

// Generate a random team name
export function generateRandomTeamName(): string {
    const word1 = TEAM_NAME_WORDS[Math.floor(Math.random() * TEAM_NAME_WORDS.length)];
    const word2 = TEAM_NAME_WORDS[Math.floor(Math.random() * TEAM_NAME_WORDS.length)];
    return `${word1.word} ${word2.word}`;
}